import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Button,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import { Google as GoogleIcon } from "@mui/icons-material";
import { ethers } from "ethers";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";
import api from "../services/api";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGoogleLogin = () => {
    window.location.href = `${process.env.REACT_APP_API_URL}/auth/google`;
  };

  const handleMetamaskLogin = async () => {
    try {
      setLoading(true);
      setError("");

      console.log("Starting Metamask login...");

      // Check if Metamask is installed
      if (!window.ethereum) {
        console.error("window.ethereum not found");
        setError(
          "Metamask is not installed. Please install Metamask extension."
        );
        return;
      }

      console.log("window.ethereum found:", {
        isMetaMask: window.ethereum.isMetaMask,
        isPhantom: window.ethereum.isPhantom,
        hasProviders: !!window.ethereum.providers,
      });

      // Detect Metamask specifically (not Phantom or other wallets)
      let metamaskProvider = null;

      if (window.ethereum.providers) {
        // Multiple wallets installed - find Metamask
        console.log("Multiple providers found, searching for Metamask...");
        metamaskProvider = window.ethereum.providers.find(
          (provider) => provider.isMetaMask && !provider.isPhantom
        );
        console.log("Metamask provider found:", !!metamaskProvider);
      } else if (window.ethereum.isMetaMask && !window.ethereum.isPhantom) {
        // Only Metamask installed
        console.log("Single Metamask provider found");
        metamaskProvider = window.ethereum;
      }

      if (!metamaskProvider) {
        console.error("Metamask provider not found");
        setError(
          "Metamask not detected. Please make sure Metamask is installed and enabled."
        );
        return;
      }

      console.log("Requesting accounts...");
      const provider = new ethers.BrowserProvider(metamaskProvider);
      const accounts = await provider.send("eth_requestAccounts", []);
      const address = accounts[0];
      console.log("Account connected:", address);

      console.log("Requesting signature...");
      const signer = await provider.getSigner();
      const message = `Sign this message to login to Cube Support Desk: ${Date.now()}`;
      const signature = await signer.signMessage(message);
      console.log("Signature obtained");

      console.log("Sending authentication request to backend...");
      const response = await api.post("/auth/metamask", {
        address,
        signature,
        message,
      });

      console.log("Backend response:", response.data);

      if (response.data.success) {
        console.log("Login successful, redirecting to dashboard");
        login(response.data.token, response.data.user);
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Metamask login error:", error);
      console.error("Error details:", {
        message: error.message,
        code: error.code,
        response: error.response?.data,
      });
      setError(
        error.response?.data?.message ||
          error.message ||
          "Failed to login with Metamask"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs" sx={{ mt: 8 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <LanguageSwitcher />
      </Box>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography component="h1" variant="h4" align="center" gutterBottom>
          {t("login.title")}
        </Typography>
        <Typography
          variant="body2"
          align="center"
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          {t("login.withGoogle")} / {t("login.withMetamask")}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 3 }}>
          {t("login.accountNotice")}
        </Alert>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleLogin}
            fullWidth
          >
            {t("login.withGoogle")}
          </Button>

          <Button
            variant="outlined"
            size="large"
            onClick={handleMetamaskLogin}
            disabled={loading}
            fullWidth
          >
            {loading ? t("common.loading") : t("login.withMetamask")}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login;
